import { useCallback, useEffect, useRef, useState } from 'react';
import type { PlayingScreen, Watching } from '../../account/api';
import { useOptionalAccount } from '../../account/AccountGate';
import { setStreamTicket } from '../../network';

const HEARTBEAT_MS = 30 * 1000;
const STATUS_SETTLE_MS = 1500;
/** While waiting in the trial pool's queue: ask again this often (also keeps the place). */
const QUEUE_POLL_MS = 10 * 1000;

export type LeaseState =
  | { kind: 'starting' }
  | { kind: 'ready' }
  /** `startsAt`: local time (ms) the trial is expected to start, for the countdown. */
  | { kind: 'queued'; position: number; startsAt: number }
  | { kind: 'replaced' }
  /** All of the plan's screens are playing elsewhere: the viewer picks one to stop (`takeOver(leaseId)`). */
  | { kind: 'full'; screens: PlayingScreen[]; maxStreams: number }
  | { kind: 'ended' };

/**
 * Holds a stream slot while the player is open (Standard: 1 at a time,
 * Premium: 2). When all of them are playing elsewhere, nothing starts and the
 * state is `full`, listing those screens; `takeOver(leaseId)` stops the one the
 * viewer picked and plays here. A screen stopped that way becomes `replaced`;
 * its `takeOver()` asks again (and so may show the list).
 *
 * Accounts on the line pool must get a line before anything plays, again on
 * each zap since viewers of one live channel share its line (at once
 * for subscribers; trials may be `queued`, first come first served, with an
 * estimate), and the line's relay pass is renewed with each heartbeat. A
 * trial moved off its line for a subscriber asks again by itself. At the end
 * of the plan the state becomes `ended`.
 * Other accounts play right away; network failures never interrupt them.
 */
export function usePlaybackLease(watching: Watching): { state: LeaseState; ready: boolean; takeOver: (stop?: string) => void } {
  const playback = useOptionalAccount()?.playback;
  const playbackRef = useRef(playback);
  playbackRef.current = playback;
  // What is playing travels with each check-in, for the admin page's "Watching now".
  const watchingRef = useRef(watching);
  watchingRef.current = watching;
  const pooled = playback?.pooled ?? false;
  const lease = useRef<string | null>(null);
  /** On a zap (line pool): the stream the new one replaces, so another screen's stream isn't ended instead. */
  const handover = useRef<string | null>(null);
  /** The screen the viewer chose to stop, sent with the next claim. */
  const stopping = useRef<string | null>(null);
  const [state, setState] = useState<LeaseState>({ kind: pooled ? 'starting' : 'ready' });
  /** The channel the current line was given for: until the line for a new channel arrives, nothing plays. */
  const [granted, setGranted] = useState<string | undefined>(undefined);
  const [generation, setGeneration] = useState(0);
  // On the line pool each live channel may be on another line (viewers of a channel share one).
  const stream = pooled ? watching.stream : undefined;

  useEffect(() => {
    const api = playbackRef.current;
    if (!api) return undefined;
    let active = true;
    let queueTimer: ReturnType<typeof setTimeout> | null = null;
    const claimedFor = stream;

    const claim = async () => {
      const replaces = handover.current ?? undefined;
      handover.current = null;
      const stop = stopping.current ?? undefined;
      stopping.current = null;
      const result = await api.start(watchingRef.current, replaces, stop);
      if (!active) {
        if (result.ok && !result.data.queued && !result.data.full) api.stop(result.data.leaseId);
        return;
      }
      if (!result.ok) {
        // Not pooled: play anyway (the server may be unreachable). Pooled: try again shortly.
        if (pooled) queueTimer = setTimeout(() => void claim(), QUEUE_POLL_MS);
        else setState({ kind: 'ready' });
        return;
      }
      if (result.data.full) {
        setState({ kind: 'full', screens: result.data.screens, maxStreams: result.data.maxStreams });
        return;
      }
      if (result.data.queued) {
        const seconds = result.data.etaSeconds ?? result.data.etaMinutes * 60;
        setState({ kind: 'queued', position: result.data.position, startsAt: Date.now() + seconds * 1000 });
        queueTimer = setTimeout(() => void claim(), QUEUE_POLL_MS);
        return;
      }
      lease.current = result.data.leaseId;
      if (result.data.ticket) setStreamTicket(result.data.ticket);
      setGranted(claimedFor);
      setState({ kind: 'ready' });
    };
    void claim();

    const heartbeat = setInterval(() => {
      const id = lease.current;
      if (!id) return;
      void api.heartbeat(id, watchingRef.current).then((result) => {
        if (!active || !result.ok) return;
        if (result.data.ok) {
          if (result.data.ticket) setStreamTicket(result.data.ticket);
          return;
        }
        lease.current = null;
        setStreamTicket(null);
        // Moved off the line (a subscriber needed this trial's line, or lots of buffering got this screen
        // a new line of its own): ask for another one (or a place in the queue).
        if (result.data.reason === 'bumped') {
          setState({ kind: 'starting' });
          setGeneration((n) => n + 1);
          return;
        }
        setState({ kind: result.data.reason === 'ended' ? 'ended' : 'replaced' });
      });
    }, HEARTBEAT_MS);

    return () => {
      active = false;
      clearInterval(heartbeat);
      if (queueTimer) clearTimeout(queueTimer);
      // A zap hands this stream over to the next claim; closing the player ends it.
      if (lease.current && pooled && watchingRef.current.stream !== claimedFor) handover.current = lease.current;
      else if (lease.current) api.stop(lease.current);
      lease.current = null;
      setStreamTicket(null);
    };
  }, [generation, pooled, stream]);

  // A new channel or episode, or a change between playing / loading / no signal, is reported
  // right away rather than at the next check-in (after a moment, so brief flickers aren't sent).
  useEffect(() => {
    const timer = setTimeout(() => {
      const id = lease.current;
      if (id) void playbackRef.current?.heartbeat(id, { title: watching.title, kind: watching.kind, status: watching.status });
    }, STATUS_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [watching.title, watching.kind, watching.status]);

  const takeOver = useCallback(
    (stop?: string) => {
      stopping.current = stop ?? null;
      setState({ kind: 'starting' });
      setGeneration((n) => n + 1);
    },
    [],
  );

  return { state, ready: state.kind === 'ready' && (!pooled || granted === stream), takeOver };
}
